import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { storage } from "./firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TabButton = ({ active, onClick, children }) => (
  <button 
    onClick={onClick} 
    className={`px-4 py-2 rounded-lg font-medium transition-colors ${active ? "bg-red-500 text-white" : "text-white/60 hover:bg-white/10"}`}
  >
    {children}
  </button>
);

export default function ContentManager() {
  const [activeTab, setActiveTab] = useState('videos');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${activeTab}`);
      setItems(res.data);
    } catch (err) {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
    setIsEditing(false);
    setFormData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API}/admin/${activeTab}/${id}`);
      fetchItems();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await axios.put(`${API}/admin/${activeTab}/${formData.id}`, formData);
      } else {
        await axios.post(`${API}/admin/${activeTab}`, formData);
      }
      setIsEditing(false);
      setFormData({});
      fetchItems();
    } catch (err) {
      alert("Failed to save");
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const storageRef = ref(storage, `videos/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed', 
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      }, 
      (error) => {
        alert("Upload failed");
        setUploadProgress(0);
      }, 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setFormData({ ...formData, url: downloadURL, title: file.name.split('.')[0] });
        setUploadProgress(0);
      }
    );
  };

  const renderForm = () => {
    if (activeTab === 'videos') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/60 mb-1">Video Title</label>
            <input type="text" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Upload Video</label>
            <input type="file" accept="video/*" onChange={handleVideoUpload} className="input-field w-full" />
            {uploadProgress > 0 && <div className="text-red-500 text-sm mt-1">Uploading: {Math.round(uploadProgress)}%</div>}
            {formData.url && <div className="text-green-500 text-sm mt-1">Video ready! URL: {formData.url}</div>}
          </div>
          <div>
            <label className="block text-white/60 mb-1">Active</label>
            <input type="checkbox" checked={formData.active !== false} onChange={e => setFormData({...formData, active: e.target.checked})} />
          </div>
          <button type="submit" className="btn-primary" disabled={uploadProgress > 0}>Save Video</button>
          <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary ml-2">Cancel</button>
        </form>
      );
    }
    if (activeTab === 'packages') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white/60 mb-1">Name</label>
              <input type="text" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field w-full" required />
            </div>
            <div>
              <label className="block text-white/60 mb-1">Price (₹)</label>
              <input type="number" value={formData.price || 0} onChange={e => setFormData({...formData, price: Number(e.target.value)})} className="input-field w-full" required />
            </div>
          </div>

          {/* Discount Section */}
          <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
            <p className="text-green-400 font-semibold text-sm mb-3">💸 Discount Settings</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 mb-1">Discount % (0 = no discount)</label>
                <input type="number" min="0" max="100" value={formData.discount || 0} onChange={e => setFormData({...formData, discount: Number(e.target.value)})} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-white/60 mb-1">Discount Label (e.g. "15% OFF")</label>
                <input type="text" placeholder="Leave blank to auto-generate" value={formData.discount_label || ''} onChange={e => setFormData({...formData, discount_label: e.target.value})} className="input-field w-full" />
              </div>
            </div>
            {formData.discount > 0 && (
              <p className="text-green-400 text-xs mt-2">
                ✅ Discounted price: ₹{Math.round((formData.price || 0) * (1 - formData.discount / 100)).toLocaleString()} 
                &nbsp;(was ₹{(formData.price || 0).toLocaleString()})
              </p>
            )}
          </div>

          <div>
            <label className="block text-white/60 mb-1">Category (standard / wedding)</label>
            <select value={formData.category || 'standard'} onChange={e => setFormData({...formData, category: e.target.value})} className="input-field w-full">
              <option value="standard">Standard</option>
              <option value="wedding">Wedding</option>
            </select>
          </div>
          <div>
            <label className="block text-white/60 mb-1">Features (one per line)</label>
            <textarea rows="5" value={(formData.features || []).join('\n')} onChange={e => setFormData({...formData, features: e.target.value.split('\n').map(f => f.trim()).filter(Boolean)})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Description</label>
            <input type="text" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} className="input-field w-full" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-white/60"><input type="checkbox" checked={formData.popular || false} onChange={e => setFormData({...formData, popular: e.target.checked})} /> Popular</label>
            <label className="flex items-center gap-2 text-white/60"><input type="checkbox" checked={formData.premium || false} onChange={e => setFormData({...formData, premium: e.target.checked})} /> Premium</label>
            <label className="flex items-center gap-2 text-white/60"><input type="checkbox" checked={formData.active !== false} onChange={e => setFormData({...formData, active: e.target.checked})} /> Active</label>
          </div>
          <button type="submit" className="btn-primary">Save Package</button>
          <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary ml-2">Cancel</button>
        </form>
      );
    }
    if (activeTab === 'addons') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/60 mb-1">Name</label>
            <input type="text" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Price</label>
            <input type="number" value={formData.price || 0} onChange={e => setFormData({...formData, price: Number(e.target.value)})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Icon (e.g. Clock, Film, HardDrive, Zap)</label>
            <input type="text" value={formData.icon || ''} onChange={e => setFormData({...formData, icon: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Active</label>
            <input type="checkbox" checked={formData.active !== false} onChange={e => setFormData({...formData, active: e.target.checked})} />
          </div>
          <button type="submit" className="btn-primary">Save Add-on</button>
          <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary ml-2">Cancel</button>
        </form>
      );
    }
    
    if (activeTab === 'faqs') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/60 mb-1">Question</label>
            <input type="text" value={formData.question || ''} onChange={e => setFormData({...formData, question: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Answer</label>
            <textarea value={formData.answer || ''} onChange={e => setFormData({...formData, answer: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Active</label>
            <input type="checkbox" checked={formData.active !== false} onChange={e => setFormData({...formData, active: e.target.checked})} />
          </div>
          <button type="submit" className="btn-primary">Save FAQ</button>
          <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary ml-2">Cancel</button>
        </form>
      );
    }
    
    if (activeTab === 'offers') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/60 mb-1">Title</label>
            <input type="text" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Description</label>
            <textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Discount Code (Optional)</label>
            <input type="text" value={formData.discount_code || ''} onChange={e => setFormData({...formData, discount_code: e.target.value})} className="input-field w-full" />
          </div>
          <div>
            <label className="block text-white/60 mb-1">Active</label>
            <input type="checkbox" checked={formData.active !== false} onChange={e => setFormData({...formData, active: e.target.checked})} />
          </div>
          <button type="submit" className="btn-primary">Save Offer</button>
          <button type="button" onClick={() => setIsEditing(false)} className="btn-secondary ml-2">Cancel</button>
        </form>
      );
    }
  };

  return (
    <div className="glass-card p-6 rounded-2xl">
      <h2 className="text-2xl font-bold mb-6">Content Manager</h2>
      
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        <TabButton active={activeTab === 'videos'} onClick={() => setActiveTab('videos')}>Videos</TabButton>
        <TabButton active={activeTab === 'packages'} onClick={() => setActiveTab('packages')}>Packages</TabButton>
        <TabButton active={activeTab === 'addons'} onClick={() => setActiveTab('addons')}>Add-ons</TabButton>
        <TabButton active={activeTab === 'faqs'} onClick={() => setActiveTab('faqs')}>FAQs</TabButton>
        <TabButton active={activeTab === 'offers'} onClick={() => setActiveTab('offers')}>Offers</TabButton>
      </div>

      {isEditing ? (
        <div className="bg-white/5 p-6 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-4">{formData.id ? 'Edit Item' : 'Add New Item'}</h3>
          {renderForm()}
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold capitalize">{activeTab}</h3>
            <button onClick={() => { setFormData({}); setIsEditing(true); }} className="btn-primary text-sm py-2 px-4">+ Add New</button>
          </div>
          
          {loading ? (
            <div className="py-8 text-center text-white/60">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-white/60">No items found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-white/50 text-sm">
                    <th className="p-3">ID / Title</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-3">
                        <div className="font-medium flex items-center gap-2">
                          {item.title || item.name || item.question || item.id}
                          {item.discount > 0 && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">
                              {item.discount_label || `${item.discount}% OFF`}
                            </span>
                          )}
                        </div>
                        <div className="text-white/40 text-xs truncate max-w-md">
                          {item.price ? `₹${item.price.toLocaleString()}` : ''} {item.description || item.url || item.answer}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <button onClick={() => { setFormData(item); setIsEditing(true); }} className="text-blue-400 hover:text-blue-300 text-sm mr-4">Edit</button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300 text-sm">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
